/* eslint-disable sonarjs/cognitive-complexity */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Field, reduxForm } from 'redux-form';
import RichText from '@tcp/core/src/components/common/atoms/RichText';
import SpinnerOverlay from '@tcp/core/src/components/common/atoms/SpinnerOverlay';
import emailSignupAbstractor from '@tcp/core/src/services/abstractors/common/EmailSmsSignup';
import BodyCopy from '../../../../../../common/atoms/BodyCopy';
import Button from '../../../../../../common/atoms/Button';
import withStyles from '../../../../../../common/hoc/withStyles';
import styles from '../styles/CreateAccountOtpForm.styles';
import OTPInputView from '../../../../OTPInput/views/OTPInput.view';
import Anchor from '../../../../../../common/atoms/Anchor';
import TextBox from '../../../../../../common/atoms/TextBox';
import Recaptcha from '../../../../../../common/molecules/recaptcha/recaptcha';
import createValidateMethod from '../../../../../../../utils/formValidation/createValidateMethod';
import getStandardConfig from '../../../../../../../utils/formValidation/validatorStandardConfig';
import {
  getLabelFromCopyOrFromTranslation,
  renderLabel,
  getDynamicLabelValues,
} from '../../../../../../../utils/labels/labelUtilty';
import {
  GLOBAL_PASSWORD_LABELS,
  GlobalPasswordsLabels,
} from '../../../../../../../utils/labels/globalPassword.labels';
import {
  GLOBAL_HELPCENTER_LABELS,
  GlobalHelpCenterLabels,
} from '../../../../../../../utils/labels/globalHelpCenter.labels';
import { getIconPath, isCanada, isMobileWeb, routerPush } from '../../../../../../../utils';
import Image from '../../../../../../common/atoms/Image';
import { setContactFormValuesInSS } from '../../../../../CnC/SinglePageCheckout/molecules/ContactForm/ContactForm.util';
import {
  GLOBAL_LOGIN_LABELS,
  GlobalLoginLabels,
  lblInvalidVerifiedEmail,
} from '../../../../../../../utils/labels/globalLogin.labels';
import {
  GLOBAL_ACTIONS_LABELS,
  GlobalActionsLabels,
  lblSubmit,
} from '../../../../../../../utils/labels/globalActions.labels';

const isRegisterToCheckout = (isHideLoginLoader) => {
  if (isHideLoginLoader) return true;
  return (
    window?.location?.href?.includes('/register') || window?.location?.href?.includes('/login')
  );
};

// eslint-disable-next-line complexity
const CreateAccountOtpForm = ({
  errorMessage,
  isLoginLoading,
  handleSubmit,
  change,
  generateOTP,
  newCodeSent,
  loginErrorMessage,
  resetLoginState,
  pristine,
  className,
  invalid,
  emailAddress,
  isLoginModal,
  isFromCheckout,
  routeForBagCheckout,
  saveExistingUserResponse,
  otpToken,
  setIsFromCreateAccount,
  otpLimit,
  locked,
  showRecaptcha,
  setShowVerificationErrorAction,
  lockTime,
  isHideLoginLoader,
  setBriteErrorMessageData,
  setShowBriteErrorMessage,
  isBriteVerifyForLoginEnabled,
}) => {
  const [resetPinField, setResetPinField] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);
  const [loginClicked, setLoginClicked] = useState(false);
  const [otp, setOtp] = useState('');
  const handleDisabled = () => {
    setIsDisabled(otp.length < 6);
  };

  const { lbl_resend_it: lblResendIt, lbl_phone_otp_not_received_content: lblPhoneOtpNotReceived } =
    getLabelFromCopyOrFromTranslation(GLOBAL_PASSWORD_LABELS, GlobalPasswordsLabels);
  const { lbl_merge_new_code_sent: lblMergeNewCodeSent } = getLabelFromCopyOrFromTranslation(
    GLOBAL_HELPCENTER_LABELS,
    GlobalHelpCenterLabels
  );

  const {
    lbl_login_checkout_as_guest: lblLoginCheckoutAsGuest,
    lbl_login_or: lblLoginOr,
    lbl_login_differentEmail: lblLoginDifferentEmail,
    'lbl_login_error_.MULTIPLE_OTP_ATTEMPT': lockedErrorMessage,
  } = getLabelFromCopyOrFromTranslation(GLOBAL_LOGIN_LABELS, GlobalLoginLabels);

  const resetError = () => {
    if (loginErrorMessage) {
      resetLoginState();
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (isLoginLoading === false) {
      setLoginClicked(false);
      change('recaptchaToken', '');
    }
  }, [isLoginLoading]);

  useEffect(() => {
    if (resetPinField) {
      setOtp('');
      setResetPinField(false);
    }
    handleDisabled();
    change('otp', otp);
  }, [otp, resetPinField]);

  const generateNewCode = (payload) => {
    resetError();
    generateOTP(payload);
    setOtp('');
  };

  const shouldLoginButtonDisabled = () => {
    return (
      !isRegisterToCheckout(isHideLoginLoader) ||
      isDisabled ||
      isLoginLoading ||
      pristine ||
      invalid
    );
  };

  const getActiveClassName = () => {
    if (loginErrorMessage) {
      return 'invalid-otp';
    }
    if (errorMessage && errorMessage.size > 0) {
      return 'invalid-otp';
    }
    if (otp && otp.length === 0) {
      return '';
    }
    if (otp && otp.length === 6) {
      return 'valid-otp';
    }
    return '';
  };

  const renderHeaderBrands = () => {
    return (
      <>
        {!isCanada() ? (
          <BodyCopy component="div" textAlign="center" className="quad-brand-icon">
            <Image
              src={getIconPath('header__brand-tab-quad')}
              alt="pjp brand logo"
              height="39px"
              width="289px"
            />
          </BodyCopy>
        ) : (
          <BodyCopy component="div" textAlign="center" className="quad-brand-icon">
            <Image
              src={getIconPath('header__brand-tab-tcp')}
              alt="tcp brand logo"
              height="24px"
              width="70px"
              className="elem-mr-MED"
            />
            <Image
              src={getIconPath('header__brand-tab-gym')}
              alt="gym brand logo"
              height="27px"
              width="72px"
              className="elem-mr-MED"
            />
          </BodyCopy>
        )}
      </>
    );
  };

  const onClickDifferentEmail = (e) => {
    e.preventDefault();
    saveExistingUserResponse('');
    setContactFormValuesInSS();
    resetError();
    routerPush('/login-checkout', '/login-checkout');
    setShowVerificationErrorAction(false);
    if (isBriteVerifyForLoginEnabled) {
      setShowBriteErrorMessage(false);
      setBriteErrorMessageData(false);
    }
  };

  const recaptchaContent = () => {
    return (
      <BodyCopy component="div">
        <Recaptcha
          verifyCallback={(token) => change('recaptchaToken', token)}
          expiredCallback={() => change('recaptchaToken', '')}
        />
        <Field
          id="recaptchaToken"
          component={TextBox}
          type="hidden"
          name="recaptchaToken"
          data-locator="otp-recaptchcb"
        />
      </BodyCopy>
    );
  };

  const handleContinueAsGuest = () => {
    setShowBriteErrorMessage(false);
    return emailSignupAbstractor
      .verifyEmail(emailAddress)
      .then((status) => {
        if (status && status.error) {
          return Promise.reject();
        }
        routeForBagCheckout();
        return Promise.resolve();
      })
      .catch(() => {
        setBriteErrorMessageData(
          getLabelFromCopyOrFromTranslation(
            `${GLOBAL_LOGIN_LABELS}.${lblInvalidVerifiedEmail}`,
            GlobalLoginLabels
          )
        );
        setShowBriteErrorMessage(true);
      });
  };

  const lockTimeValue = lockTime || '30';
  return (
    <>
      {isLoginLoading && !loginClicked && (
        <SpinnerOverlay isLoginModal={isLoginModal} isNewLoginScreen={true} />
      )}
      <div className={className}>
        <form name="OTPForm" onSubmit={handleSubmit} noValidate>
          <BodyCopy
            component="p"
            fontSize={isMobileWeb() ? 'fs18' : 'fs28'}
            fontWeight="extrabold"
            fontFamily="secondary"
            className="otp-form-header elem-mt-XXXL"
            textAlign="center"
          >
            {otpLimit || locked
              ? 'Checkout'
              : `${getDynamicLabelValues('dynamic.account_d.lbl_create_acc_otp_header')} `}
          </BodyCopy>
          {!otpLimit && !locked && (
            <BodyCopy
              component="p"
              fontSize={isMobileWeb() ? 'fs16' : 'fs28'}
              fontWeight="extrabold"
              fontFamily="secondary"
              className="otp-form-header"
              textAlign="center"
            >
              {emailAddress}
            </BodyCopy>
          )}
          {renderHeaderBrands()}
          {emailAddress && isFromCheckout && (
            <>
              <BodyCopy
                textAlign="center"
                fontWeight="extrabold"
                fontSize="fs13"
                fontFamily="secondary"
                className="elem-mt-XL "
              >
                {emailAddress}
              </BodyCopy>
              <BodyCopy component="div" textAlign="center" className="elem-mb-MED">
                <Anchor
                  noLink
                  underline
                  fontSizeVariation="large"
                  fontSize="fs14"
                  anchorVariation="primary"
                  dataLocator="Use a Different Email"
                  onClick={onClickDifferentEmail}
                  className="different-email-link"
                >
                  {lblLoginDifferentEmail}
                </Anchor>
              </BodyCopy>
            </>
          )}
          {!otpLimit && !locked && (
            <>
              <BodyCopy component="div" textAlign="center" className="create-account-otp-wrapper">
                <BodyCopy
                  component="p"
                  fontSize={isMobileWeb() ? 'fs12' : 'fs14'}
                  fontWeight="bold"
                  fontFamily="secondary"
                  textAlign="center"
                  className="elem-mb-MED create-account-otp-sub-heading"
                >
                  {getDynamicLabelValues('dynamic.account_d.lbl_create_acc_otp_sub_header')}
                </BodyCopy>

                <BodyCopy component="div" textAlign="center" className="otp-wrapper">
                  <OTPInputView
                    value={otp}
                    onChange={setOtp}
                    numInputs={6}
                    separator={<span className="seperator">-</span>}
                    inputStyle={getActiveClassName()}
                    containerStyle="otp-container"
                    className="otp-input-container"
                    isInputNum
                    shouldAutoFocus
                    isResetViaText
                  />
                </BodyCopy>
                {!loginErrorMessage && newCodeSent && (
                  <BodyCopy
                    fontSize="fs12"
                    fontWeight="extrabold"
                    color="green.500"
                    className="elem-mt-XS test-newCode otp-message"
                    fontFamily="secondary"
                  >
                    {lblMergeNewCodeSent}
                  </BodyCopy>
                )}
                {loginErrorMessage && (
                  <BodyCopy
                    fontSize="fs12"
                    fontWeight="extrabold"
                    color="red.500"
                    className="login-error otp-message"
                    fontFamily="secondary"
                  >
                    <RichText className="richTextColor" richTextHtml={loginErrorMessage} />
                  </BodyCopy>
                )}
              </BodyCopy>

              {showRecaptcha && !otpLimit && !locked && (
                <BodyCopy component="div" className="recaptcha_loginotp elem-mt-MED">
                  {recaptchaContent()}
                </BodyCopy>
              )}

              <BodyCopy component="div" textAlign="center">
                <Button
                  className="create-acc-otp-button elem-mt-MED"
                  id="subBtn"
                  fill="BLUE"
                  type="submit"
                  buttonVariation="fixed-width"
                  dataLocator="login-logincta"
                  fullWidth
                  showBorderRadiusStyle
                  disabled={shouldLoginButtonDisabled()}
                  loading={isLoginLoading && loginClicked}
                  spc
                  onClick={() => {
                    setLoginClicked(true);
                    setIsFromCreateAccount(true);
                  }}
                >
                  {renderLabel(
                    getLabelFromCopyOrFromTranslation(
                      `${GLOBAL_ACTIONS_LABELS}.${lblSubmit}`,
                      GlobalActionsLabels
                    ),
                    'lbl_submit'
                  )?.toUpperCase()}
                </Button>
              </BodyCopy>
            </>
          )}
          {(otpLimit || locked) && (
            <BodyCopy
              fontSize="fs12"
              fontWeight="extrabold"
              color="red.500"
              className="login-error otp-message otp-limit-message"
              fontFamily="secondary"
            >
              {(locked && otpLimit) || otpLimit
                ? getDynamicLabelValues(
                    'dynamic.account_d.lbl_createAccount_checkout_error_checkout_ACCOUNT_VERIFY_OTP_COUNT_EXCEEDED'
                  )
                : lockedErrorMessage && lockedErrorMessage.replace('#lockTime', lockTimeValue)}
            </BodyCopy>
          )}
          {isFromCheckout && (
            <>
              {!otpLimit && !locked && (
                <BodyCopy
                  className="elem-mb-SM"
                  component="p"
                  textAlign="center"
                  fontSize={isMobileWeb() ? 'fs14' : 'fs16'}
                  fontWeight={isMobileWeb() ? 'semibold' : 'bold'}
                  fontFamily="secondary"
                >
                  {lblLoginOr}
                </BodyCopy>
              )}
              <div className="checkout-guest-button-container">
                <Button
                  fill="WHITE"
                  type="button"
                  buttonVariation="fixed-width"
                  dataLocator="login-logincta"
                  fullWidth
                  onClick={
                    isBriteVerifyForLoginEnabled && emailAddress
                      ? handleContinueAsGuest
                      : routeForBagCheckout
                  }
                  className="rounded-corner checkout-guest-button elem-mb-MED"
                  spc
                >
                  {lblLoginCheckoutAsGuest}
                </Button>
              </div>
            </>
          )}
          {!otpLimit && !locked && (
            <BodyCopy component="div" textAlign="center" className="elem-mb-XS">
              <p className="resend-code-text">
                {lblPhoneOtpNotReceived}
                &nbsp;
                <Anchor
                  underline
                  fontSizeVariation="large"
                  anchorVariation="primary"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsFromCreateAccount(true);
                    generateNewCode({ otpChannel: 'email', token: otpToken });
                  }}
                  className="resend-it"
                >
                  {lblResendIt}
                </Anchor>
              </p>
            </BodyCopy>
          )}
        </form>
      </div>
    </>
  );
};

CreateAccountOtpForm.propTypes = {
  labels: PropTypes.shape({}).isRequired,
  errorMessage: PropTypes.string,
  change: PropTypes.func,
  loginErrorMessage: PropTypes.string,
  newCodeSent: PropTypes.bool,
};

CreateAccountOtpForm.defaultProps = {
  errorMessage: '',
  change: () => {},
  loginErrorMessage: '',
  newCodeSent: false,
};

const validateMethod = createValidateMethod(getStandardConfig(['recaptchaToken']));

export default reduxForm({
  form: 'CreateAccountOTPForm',
  enableReinitialize: true,
  ...validateMethod,
})(withStyles(CreateAccountOtpForm, styles));
export { CreateAccountOtpForm as CreateAccountOtpFormVanilla };
